<template>
    <transition
        @before-enter="appearAnimationBefore"
        @enter="appearAnimationEnter"
        @leave="appearAnimationLeave"
    >
        <div tabindex="0" class="z-modal" v-if="active">
            <div class="z-modal__overlay" @click="closeModal"></div>
            <div class="z-modal__window" :class="classObject" ref="magicHeight">
                <div class="z-modal__content">
                    <template v-if="hasSlotData">
                        <div class="z-modal__html">
                            <slot/>
                        </div>
                    </template>
                    <template v-else>
                        <div
                            class="z-modal__item"
                            v-for="(item, index) in data"
                            v-if="index === current_index"
                            :key="index"
                        >
                            <template v-if="item.type === 'photo' || item.type === 'picture'">
                                <div class="z-modal__media">
                                    <img ref="modalImage" @load="loaded" :src="item.image.full"/>
                                    <span v-if="item.name" class="z-modal__name" v-html="item.name"></span>
                                    <span v-if="item.detail" class="z-modal__caption" v-html="item.detail"></span>
                                    <span v-if="item.date" class="z-modal__date">{{ item.date | momentFilter('DD MMMM YYYY') }}</span>
                                    <span v-if="download" class="z-modal__download">
                                        <z-link
                                            class="ar-card__icon u-inline"
                                            target="_blank"
                                            content="Download image"
                                            :href="item.image.full"
                                            download
                                            v-tippy-tooltip="{
                                                theme : 'transparent',
                                                arrow: true,
                                                placement: 'top-start'
                                            }"
                                        >
                                            <z-icon name="download" width="48" height="48"></z-icon>
                                        </z-link>
                                        <p v-if="item.size" v-html="item.size"></p>
                                    </span>
                                </div>
                            </template>
                            <template v-else-if="item.type === 'video'">
                                <div class="z-modal__media z-modal__media--video">
                                    <z-video
                                        width="640px"
                                        height="390px"
                                        :title="item.name"
                                        :src="item.image.full"
                                        :volume-level="videoVolume"
                                    />
                                </div>
                            </template>
                            <template v-else-if="item.type === 'html'">
                                <div class="z-modal__html" v-if="item.detail" v-html="item.detail"></div>
                            </template>
                        </div>
                    </template>
                </div>
                <div class="z-modal__navigation" v-if="!hasSlotData && data.length > 1">
                    <button
                        class="z-modal__arrow z-modal__arrow--prev"
                        :class="{'is-disabled' : !isPrev('prev')}"
                        @click="changeSlide('prev')"
                        :title="lang === 'ru' ? 'Предыдущий' : 'Previous'"
                        role="button"
                    >
                        <z-icon name="arrow" dir="left" width="32" height="32"></z-icon>
                    </button>
                    <button
                        class="z-modal__arrow z-modal__arrow--next"
                        :class="{'is-disabled' : !isNext('next')}"
                        @click="changeSlide('next')"
                        :title="lang === 'ru' ? 'Следующий' : 'Next'"
                        role="button"
                    >
                        <z-icon name="arrow" dir="right" width="32" height="32"></z-icon>
                    </button>
                </div>
                <button
                    class="z-modal__close"
                    @click="closeModal"
                    :title="lang === 'ru' ? 'Закрыть' : 'Close'"
                    role="button"
                >
                    <z-icon name="close"></z-icon>
                </button>
            </div>
        </div>
    </transition>
</template>

<script>
import Velocity from 'velocity-animate'

export default {
    name: 'z-modal',
    props: {
        id: {
            type: String,
            required: true
        },
        data: Array,
        download: {
            type: Boolean,
            default: false
        },
        theme: {
            type: String
        },
        video: {
            type: Boolean,
            default: false
        },
        autoWidth: {
            type: Boolean,
            default: false
        },
        videoVolume: {
            type: [String, Number],
            default: 0
        }
    },
    data () {
        return {
            scrollY: 0,
            active: false,
            duration: 300,
            loading: true,
            current_index: 0,
            current_type: 'photo',
            lang: this.$root.lang,
            isHidden: false
        }
    },
    mounted () {
        const self = this
        this.$root.$bus.$on('open-modal', data => this.openModal(data))
        this.$root.$bus.$on('close-modal', data => this.closeModal(data))

        document.addEventListener('keydown', (event) => {
            if (event.keyCode === 27 && self.active) {
                self.closeModal()
            }
        }, false)
    },
    beforeDestroy () {
        this.$root.$bus.$off('open-modal', data => this.openModal(data))
        this.$root.$bus.$on('close-modal', data => this.closeModal(data))
    },
    computed: {
        hasSlotData () {
            return this.$slots.default !== undefined
        },
        classObject () {
            let arrClass = []
            if (this.theme) {
                arrClass.push(`z-modal--theme-${this.theme}`)
            }

            if ((this.current_type === 'photo' || this.current_type === 'picture') && this.autoWidth) {
                arrClass.push('z-modal__window--fix')
            }
            return arrClass
        }
    },
    methods: {
        openModal (data) {
            if (data.id !== this.id) return

            this.active = true
            this.disableScroll()
            this.current_index = data.index

            this.$emit('open')

            if (!this.hasSlotData) this.current_type = this.data[this.current_index].type
        },
        closeModal () {
            this.isHidden = false
            this.active = false
            this.enableScroll()
            this.current_index = 0
            this.$emit('close')
        },
        loaded (e) {
            console.log(e.target)
            e.target.classList.add('is-loaded')
            this.loading = false
        },
        changeSlide (type) {
            this.isHidden = false
            this.loading = true
            // eslint-disable-next-line no-unused-expressions
            this.isPrev(type) ? this.current_index-- : false
            // eslint-disable-next-line no-unused-expressions
            this.isNext(type) ? this.current_index++ : false
            this.current_type = this.data[this.current_index].type
        },
        isPrev (type) {
            return type === 'prev' && this.current_index !== 0
        },
        isNext (type) {
            return type === 'next' && this.current_index < this.data.length - 1
        },
        disableScroll () {
            if (document.body.style.position !== 'fixed') {
                this.scrollY = window.pageYOffset
                document.body.style.position = 'fixed'
                document.body.style.top = `-${this.scrollY}px`
                document.body.style.width = '100%'
            }
        },
        enableScroll () {
            document.body.style.position = ''
            document.body.style.top = ''
            document.body.style.width = ''
            window.scroll(0, this.scrollY)
        },
        appearAnimationBefore (el) {
            el.style.opacity = 0
        },
        appearAnimationEnter (el, done) {
            Velocity(el, { opacity: 1 }, { duration: this.duration }, { complete: done })
        },
        appearAnimationLeave (el, done) {
            Velocity(el, { opacity: 0 }, { duration: this.duration })
            Velocity(el, { display: 'none' }, { complete: done })
        },
        isVideoLocal (video) {
            return video.includes('/upload/')
        }
    }
}
</script>

<style lang="scss" src="./index.scss"></style>
